import React, { createContext, useContext, ReactNode } from 'react';
import { useTours, TourHook } from '@/hooks/useTours';
import { logError, logInfo } from '@/utils/errorHandler';

interface TourProviderProps {
  children: ReactNode;
}

const TourContext = createContext<TourHook | null>(null);

export function TourProvider({ children }: TourProviderProps) {
  try {
    const tourHook = useTours();
    return <TourContext.Provider value={tourHook}>{children}</TourContext.Provider>;
  } catch (error) {
    // Log error but don't break the app
    logError(
      'Tour system failed to initialize',
      {
        component: 'TourProvider',
        metadata: { error },
      },
      { showToast: false }
    );
    return <TourContext.Provider value={null}>{children}</TourContext.Provider>;
  }
}

export function useTourContext(): TourHook {
  const context = useContext(TourContext);
  if (!context) {
    // Return a safe fallback instead of throwing
    logInfo('useTourContext called outside TourProvider - returning safe fallback', {
      component: 'useTourContext',
    });
    return {
      tourState: null,
      isActiveTour: false,
      shouldShowTour: false,
      startTour: () => {},
      nextStep: () => {},
      previousStep: () => {},
      skipTour: () => {},
      completeTour: () => {},
      closeTour: () => {},
      getCurrentStepIndex: () => 0,
      getTotalSteps: () => 0,
      getStepTranslation: () => null,
      navigateToStep: () => {},
      resetTour: () => {},
      resetAllTours: () => {},
    };
  }
  return context;
}
